import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, ScrollText, BookOpen } from 'lucide-react';
import useSeo from '@/hooks/useSeo';

export default function NotFound() {
    useSeo({
        title: 'Sector Not Found',
        description: 'This room of the Lab has not been built yet.',
        path: '/404',
        noindex: true,
    });

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="not-found-page">
            <div className="max-w-2xl w-full text-center relative">
                {/* Glitch scan lines */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-30"
                    style={{
                        background:
                            'repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)',
                    }}
                />
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="relative"
                >
                    <div className="lab-overline mb-6 inline-flex items-center gap-2 text-lab-pink">
                        <AlertTriangle className="w-3 h-3" />
                        SIGNAL ERROR · SECTOR NOT FOUND
                    </div>

                    <div className="relative mb-6">
                        <motion.h1
                            className="font-display font-light text-[9rem] sm:text-[12rem] leading-none tracking-tight text-white/90 relative"
                            animate={{
                                textShadow: [
                                    '0 0 24px rgba(255,0,85,0.4)',
                                    '2px 0 24px rgba(0,240,255,0.4), -2px 0 24px rgba(176,38,255,0.4)',
                                    '0 0 24px rgba(255,0,85,0.4)',
                                ],
                            }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            404
                        </motion.h1>
                        <motion.div
                            className="absolute inset-0 font-display font-light text-[9rem] sm:text-[12rem] leading-none tracking-tight text-lab-pink/40 mix-blend-screen pointer-events-none"
                            animate={{ x: [0, 2, -1, 0], opacity: [0.4, 0.2, 0.6, 0.4] }}
                            transition={{ duration: 0.4, repeat: Infinity }}
                        >
                            404
                        </motion.div>
                    </div>

                    <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
                        Sector <span className="text-gradient-lab">not found</span>.
                    </h2>
                    <p className="text-lab-mute text-base sm:text-lg mt-4 max-w-lg mx-auto leading-relaxed">
                        This room of the Lab hasn't been built yet. The signal you followed leads nowhere — but the rest of the Lab is still online.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/"
                            data-testid="notfound-cta-home"
                            className="group inline-flex items-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-lab-blue to-lab-violet text-black font-medium text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                            Return to Facility
                        </Link>
                        <Link
                            to="/library"
                            className="inline-flex items-center gap-2 rounded-full px-6 py-3 glass hover:border-lab-blue/60 text-white text-sm transition-colors"
                        >
                            <BookOpen className="w-4 h-4 text-lab-blue" />
                            Open The Library
                        </Link>
                        <Link
                            to="/log"
                            className="inline-flex items-center gap-2 rounded-full px-6 py-3 glass hover:border-lab-violet/60 text-white text-sm transition-colors"
                        >
                            <ScrollText className="w-4 h-4 text-lab-violet" />
                            Read The Log
                        </Link>
                    </div>

                    <div className="mt-14 lab-mono text-[10px] text-lab-dim tracking-[0.25em]">
                        · · · TRANSMISSION LOST · · ·
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
